import { CollectionApiResponse } from 'api/api.types';
import { Flex, useIsMobileView } from 'deepstash-ui';
import React from 'react';
import BreadcrumbList from 'src/components/breadcrumbs/BreadcrumbList';
import Footer from 'src/components/layout/components/Footer';
import FullWidthSectionWrapper from 'src/components/layout/components/FullWidthSectionWrapper';
import Navbar from 'src/components/navbar/Navbar';
import { slugify } from 'utils/global';
import { UNSORTED_TOPICS } from 'utils/metatopics.constants';
import LandingProSection from '../landing/components-v2/LandingProSection';
import CollectionFAQSection from './components/CollectionFAQSection';
import CollectionKeyIdeasSection from './components/CollectionKeyIdeasSection';
import CollectionOverviewSection from './components/CollectionOverviewSection';
import CollectionPageHeader from './components/CollectionPageHeader';
import CollectionSignUpSection from './components/CollectionSignUpSection';
import CollectionSimilarCollectionsSection from './components/CollectionSimilarCollectionsSection';
import CollectionSkillsSection from './components/CollectionSkillsSection';
import CollectionSubscriptionSection from './components/CollectionSubscriptionSection';
import CollectionTabs, {
  COLLECTIONS_TABS_HEIGHT,
} from './components/CollectionTabs';

const TABS = ['Skills', 'Overview', 'Key Ideas', 'FAQ'];
const ID_SKILLS = 'id_skills';
const ID_OVERVIEW = 'id_overview';
const ID_KEY_IDEAS = 'id_key_ideas';
const ID_FAQ = 'id_faq';

// Used for scrollTo functions
const PT_SECTION = COLLECTIONS_TABS_HEIGHT + 16;

interface CollectionNSIPageProps {
  collection: CollectionApiResponse;
}

const CollectionNSIPage: React.FC<CollectionNSIPageProps> = ({
  collection,
}) => {
  const isMobileView = useIsMobileView();
  const [currentTabIndex, setCurrentTabIndex] = React.useState(0);

  const skillsSectionRef = React.useRef<HTMLDivElement>(null);
  const overviewSectionRef = React.useRef<HTMLDivElement>(null);
  const keyIdeasSectionRef = React.useRef<HTMLDivElement>(null);
  const faqSectionRef = React.useRef<HTMLDivElement>(null);

  const onPressToChangeTab = ({ newIndex }: { newIndex: number }) => {
    if (newIndex >= TABS.length || newIndex < 0) {
      return;
    }

    setCurrentTabIndex(prevIndex => {
      if (newIndex === 0) {
        skillsSectionRef.current?.scrollIntoView({ block: 'start' });
      } else if (newIndex === 1) {
        overviewSectionRef.current?.scrollIntoView({ block: 'start' });
      } else if (newIndex === 2) {
        keyIdeasSectionRef.current?.scrollIntoView({ block: 'start' });
      } else if (newIndex === 3) {
        faqSectionRef.current?.scrollIntoView({ block: 'start' });
      }

      if (prevIndex === newIndex) {
        return prevIndex;
      }
      return newIndex;
    });
  };

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            switch (id) {
              case ID_SKILLS:
                setCurrentTabIndex(0);
                break;
              case ID_OVERVIEW:
                setCurrentTabIndex(1);
                break;
              case ID_KEY_IDEAS:
                setCurrentTabIndex(2);
                break;
              case ID_FAQ:
                setCurrentTabIndex(3);
                break;

              default:
                break;
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.2,
      },
    );

    // Add observers for all sections
    if (skillsSectionRef.current) {
      observer.observe(skillsSectionRef.current);
    }
    if (overviewSectionRef.current) {
      observer.observe(overviewSectionRef.current);
    }
    if (keyIdeasSectionRef.current) {
      observer.observe(keyIdeasSectionRef.current);
    }
    if (faqSectionRef.current) {
      observer.observe(faqSectionRef.current);
    }

    return () => {
      // Remove observers for all sections
      if (skillsSectionRef.current) {
        observer.unobserve(skillsSectionRef.current);
      }
      if (overviewSectionRef.current) {
        observer.unobserve(overviewSectionRef.current);
      }
      if (keyIdeasSectionRef.current) {
        observer.unobserve(keyIdeasSectionRef.current);
      }
      if (faqSectionRef.current) {
        observer.unobserve(faqSectionRef.current);
      }
    };
  }, []);

  return (
    <Flex w="100%" flexDirection="column">
      <FullWidthSectionWrapper
        containerProps={
          !!collection.hashtags?.length
            ? {
                flexDirection: 'column',
                gridGap: 0,
              }
            : {}
        }
      >
        <Navbar />
        {!!collection.hashtags?.length ? (
          <BreadcrumbList
            items={[
              {
                title: 'Home',
                link: '/',
              },
              {
                title:
                  UNSORTED_TOPICS[collection.hashtags[0]]?.title ??
                  collection.hashtags[0],
                link: '/topic/' + collection.hashtags[0],
              },
              {
                title: collection.name,
                link: `/collection/${collection.id}/${slugify(
                  collection.name,
                )}`,
              },
            ]}
          />
        ) : null}
      </FullWidthSectionWrapper>
      <FullWidthSectionWrapper mt={64}>
        <CollectionPageHeader collection={collection} />
      </FullWidthSectionWrapper>
      <FullWidthSectionWrapper mt={64}>
        <CollectionSignUpSection />
      </FullWidthSectionWrapper>

      {/** TABS */}
      {!isMobileView && (
        <FullWidthSectionWrapper mt={64}>
          <CollectionTabs
            tabs={TABS}
            currentTabIndex={currentTabIndex}
            onPressToChangeTab={onPressToChangeTab}
          />
        </FullWidthSectionWrapper>
      )}

      {/** SKILLS */}
      <FullWidthSectionWrapper
        mt={isMobileView ? 60 : 64}
        pt={isMobileView ? 0 : PT_SECTION}
        ref={skillsSectionRef}
        id={ID_SKILLS}
      >
        <CollectionSkillsSection skills={collection.skills} />
      </FullWidthSectionWrapper>

      {/** OVERVIEW */}
      <FullWidthSectionWrapper
        mt={isMobileView ? 72 : 80}
        pt={isMobileView ? 0 : PT_SECTION}
        ref={overviewSectionRef}
        id={ID_OVERVIEW}
        {...(isMobileView ? { containerProps: { px: 0 } } : {})}
      >
        <CollectionOverviewSection collection={collection} />
      </FullWidthSectionWrapper>

      {/** KEY IDEAS */}
      <FullWidthSectionWrapper
        mt={isMobileView ? 72 : 80}
        pt={isMobileView ? 0 : PT_SECTION}
        ref={keyIdeasSectionRef}
        id={ID_KEY_IDEAS}
        {...(isMobileView
          ? { containerProps: { px: 0, flexDirection: 'column' } }
          : {
              containerProps: { flexDirection: 'column', alignItems: 'center' },
            })}
      >
        {collection.articles !== undefined &&
        collection.articles !== null &&
        collection.articles.length > 0 ? (
          <CollectionKeyIdeasSection
            articles={collection.articles}
            totalIdeas={collection.total_blocks}
          />
        ) : null}

        <CollectionSubscriptionSection />

        {collection.similar_collections !== undefined &&
        collection.similar_collections !== null ? (
          <CollectionSimilarCollectionsSection
            similarCollections={collection.similar_collections}
          />
        ) : null}
      </FullWidthSectionWrapper>

      {/** FAQ */}
      <FullWidthSectionWrapper
        mb={isMobileView ? 44 : 128}
        mt={isMobileView ? 72 : 80}
        pt={isMobileView ? 0 : PT_SECTION}
        ref={faqSectionRef}
        id={ID_FAQ}
        containerProps={{ flexDirection: 'column' }}
      >
        <CollectionFAQSection />
        <LandingProSection
          showProComparison={false}
          px={0}
          width={'100%'}
          landingLocation={'collection-last-section'}
        />
      </FullWidthSectionWrapper>
      <Footer />
    </Flex>
  );
};

export default CollectionNSIPage;
